<template>
  <div class="quality-breakdown-card">
    <div class="quality-breakdown-card__header">
      <h6 class="quality-breakdown-card__title">{{ quality.quality_name }}</h6>
    </div>

    <div class="quality-breakdown-card__content">
      <div class="quality-breakdown-card__chart-container">
        <donut-chart
          :labels="[quality.quality_name, 'Other']"
          :values="[quality.percentage, 100 - quality.percentage]"
          :colors="[color, PaletteColors['default-lighten-80']]"
          :show-data-labels="false"
          cutout="70%"
          :chart-options="{ aspectRatio: 1 }"
        />
        <div class="quality-breakdown-card__percentage">{{ quality.percentage }}%</div>
      </div>

      <div class="quality-breakdown-card__stat">
        <span class="quality-breakdown-card__stat-label">
          <span class="quality-breakdown-card__color-dot" :style="{ backgroundColor: color }"></span>
          {{ quality.quality_name }}
        </span>
        <span class="quality-breakdown-card__stat-value">{{ quality.percentage }}% of recognitions</span>
      </div>
    </div>

    <div v-if="quality.top_employees?.length" class="quality-breakdown-card__employees">
      <p class="quality-breakdown-card__employees-title">Top {{ quality.top_employees.length }} employees</p>
      <div
        v-for="employee in quality.top_employees"
        :key="employee.employee_id"
        class="quality-breakdown-card__employee"
      >
        <div class="quality-breakdown-card__employee-info">
          <span class="quality-breakdown-card__employee-name">{{ employee.full_name }}</span>
        </div>
        <span class="quality-breakdown-card__employee-count">{{ employee.recognitions_count }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import DonutChart from '@/components/charts/DonutChart.vue';
import { PaletteColors } from '@/Theme';
import { getDatasetColor } from '@/utils/chart';
import { computed } from 'vue';

const props = defineProps({
  quality: {
    type: Object,
    required: true,
  },
  colorIndex: {
    type: Number,
    default: 0,
  },
});

const color = computed(() => PaletteColors[getDatasetColor(props.colorIndex)]);
</script>

<style scoped lang="scss">
.quality-breakdown-card {
  background-color: var(--palette-color-base-white);
  border-radius: 4px;
  box-shadow: var(--depth-2);
  padding: 1.25rem;
  height: 100%;

  &__header {
    margin-bottom: 1rem;
  }

  &__title {
    font-weight: 600;
    font-size: 14px;
    margin: 0;
    color: var(--palette-color-default);
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 1rem;
  }

  &__chart-container {
    position: relative;
    width: 80px;
    height: 80px;
    margin-bottom: 0.75rem;
  }

  &__percentage {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 14px;
    font-weight: 600;
    color: var(--palette-color-default);
  }

  &__stat {
    text-align: center;
  }

  &__stat-label {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    font-size: 12px;
    font-weight: 500;
    color: var(--palette-color-default);
    margin-bottom: 0.25rem;
  }

  &__color-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__stat-value {
    font-size: 11px;
    color: var(--palette-color-default-lighten-30);
  }

  &__employees {
    border-top: 1px solid var(--palette-color-default-lighten-90);
    padding-top: 0.75rem;
  }

  &__employees-title {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--palette-color-default-lighten-30);
    margin-bottom: 0.5rem;
  }

  &__employee {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.375rem 0;

    &:not(:last-child) {
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
    }
  }

  &__employee-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }

  &__employee-name {
    font-size: 12px;
    color: var(--palette-color-default);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__employee-count {
    font-size: 12px;
    font-weight: 600;
    color: var(--palette-color-default);
    flex-shrink: 0;
  }
}
</style>
